"use client";

import { useCallback, useMemo, useState } from "react";
import { PillGroup } from "@/components/tool-kit/primitives";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  type ExhibitionLabels,
  type ExhibitionSource,
  exhibitionIsReady,
  EXHIBITION_FIELDS as F,
  formatExhibition,
} from "@/lib/tool-kit/media-sources/exhibition-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const EXHIBITION_CITATION_GENERATOR_STRINGS = {
  formHeading: "Exhibition details",
  resultsHeading: "Your citations",
  emptyPromptExhibition: "Enter the exhibition's title to see your citations.",
  emptyPromptCatalog: "Enter the catalog's title to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  sourceLabel: "What are you citing?",
  sourceExhibition: "The exhibition",
  sourceCatalog: "Its catalog",
  titleLabel: "Exhibition title",
  titlePlaceholder:
    "Design for eternity: Architectural models from the ancient Americas",
  catalogTitleLabel: "Catalog title",
  titleHint:
    "Type it in sentence case: capitalize only the first word, the first word after a colon and proper nouns. MLA and Chicago get title case automatically.",
  curatorsLabel: "Curator(s), if the museum credits them",
  curatorsPlaceholder: "Jean-Luc Martinez",
  curatorsHint:
    "One per line. APA puts the curators first; without them, the title leads.",
  artistLabel: "Artist, for a show of one artist's work (MLA)",
  artistPlaceholder: "Nick Cave",
  artistHint: "MLA and Chicago then start with the artist.",
  museumLabel: "Museum or gallery",
  museumPlaceholder: "The Met Fifth Avenue",
  cityLabel: "City",
  cityPlaceholder: "New York",
  regionLabel: "State or province (APA)",
  regionPlaceholder: "NY",
  countryLabel: "Country (APA)",
  countryPlaceholder: "United States",
  startDateLabel: "Opening date",
  endDateLabel: "Closing date",
  datePlaceholder: "YYYY-MM-DD",
  urlLabel: "URL of the exhibition page (optional)",
  urlPlaceholder:
    "https://www.metmuseum.org/exhibitions/listings/2015/design-for-eternity",
  authorsLabel: "Author(s) or editor(s)",
  authorsPlaceholder: "Joanne Pillsbury",
  authorsHint:
    "One per line. Leave blank if the catalog credits only the museum.",
  authorRoleLabel: "They are the",
  authorRoleAuthors: "Authors",
  authorRoleEditors: "Editors",
  publisherLabel: "Publisher",
  publisherPlaceholder: "The Metropolitan Museum of Art",
  yearLabel: "Year published",
  yearPlaceholder: "2015",
  pageLabel: "Page you cite (Chicago notes)",
  pagePlaceholder: "24",
  exhibitionTitleLabel: "Exhibition it accompanied (optional, Chicago)",
  exhibitionTitlePlaceholder:
    "Design for eternity: Architectural models from the ancient Americas",
  exhibitionVenueLabel: "Where that exhibition was shown (optional, Chicago)",
  exhibitionVenuePlaceholder: "the Metropolitan Museum of Art, New York",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 (§10.14) adds [Exhibition] after the title, dates the exhibition by its year or range of years, and gives the museum's name and location.",
  mlaNote:
    "MLA 9 gives the exhibition's opening and closing dates, then the museum and city. A show of one artist's work starts with the artist.",
  chicagoNote:
    "The Chicago Manual has no standard form for an exhibition: give its italic title and enough detail to identify it, in the text or a note. List the catalog, not the event, in your bibliography.",
  apaCatalogNote:
    "APA 7 cites an exhibition catalog as a book: authors or editors, year, italic title and publisher.",
  mlaCatalogNote:
    "MLA 9 cites an exhibition catalog the way it cites any book.",
  chicagoCatalogNote:
    "Chicago 18 (§14.134) cites an exhibition catalog like a book; exh. cat. marks it in a note, and the bibliography can say which exhibition it accompanied.",
  exampleExhibition: {
    title:
      "Design for eternity: Architectural models from the ancient Americas",
    museum: "The Met Fifth Avenue",
    city: "New York",
    region: "NY",
    country: "United States",
    startDate: "2015-10-26",
    endDate: "2016-09-18",
    url: "https://www.metmuseum.org/exhibitions/listings/2015/design-for-eternity",
  },
  exampleCatalog: {
    authors:
      "Joanne Pillsbury\nPatricia Joan Sarro\nJames Doyle\nJuliet Wiersema",
    authorRole: "authors",
    title:
      "Design for eternity: Architectural models from the ancient Americas",
    publisher: "The Metropolitan Museum of Art",
    year: "2015",
    exhibitionTitle:
      "Design for eternity: Architectural models from the ancient Americas",
    exhibitionVenue: "the Metropolitan Museum of Art, New York",
  },
} as const;

type Strings = typeof EXHIBITION_CITATION_GENERATOR_STRINGS;

function exhibitionFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
    {
      id: F.curators,
      kind: "people",
      label: strings.curatorsLabel,
      placeholder: strings.curatorsPlaceholder,
      hint: strings.curatorsHint,
    },
    {
      id: F.artist,
      kind: "text",
      label: strings.artistLabel,
      placeholder: strings.artistPlaceholder,
      hint: strings.artistHint,
    },
    {
      id: F.museum,
      kind: "text",
      label: strings.museumLabel,
      placeholder: strings.museumPlaceholder,
    },
    {
      id: F.city,
      kind: "text",
      label: strings.cityLabel,
      placeholder: strings.cityPlaceholder,
    },
    {
      id: F.region,
      kind: "text",
      label: strings.regionLabel,
      placeholder: strings.regionPlaceholder,
    },
    {
      id: F.country,
      kind: "text",
      label: strings.countryLabel,
      placeholder: strings.countryPlaceholder,
    },
    {
      id: F.startDate,
      kind: "date",
      label: strings.startDateLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.endDate,
      kind: "date",
      label: strings.endDateLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      wide: true,
    },
  ];
}

function catalogFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.authors,
      kind: "people",
      label: strings.authorsLabel,
      placeholder: strings.authorsPlaceholder,
      hint: strings.authorsHint,
    },
    {
      id: F.authorRole,
      kind: "select",
      label: strings.authorRoleLabel,
      options: [
        { value: "authors", label: strings.authorRoleAuthors },
        { value: "editors", label: strings.authorRoleEditors },
      ],
    },
    {
      id: F.title,
      kind: "text",
      label: strings.catalogTitleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
    {
      id: F.publisher,
      kind: "text",
      label: strings.publisherLabel,
      placeholder: strings.publisherPlaceholder,
    },
    {
      id: F.year,
      kind: "text",
      label: strings.yearLabel,
      placeholder: strings.yearPlaceholder,
    },
    {
      id: F.page,
      kind: "text",
      label: strings.pageLabel,
      placeholder: strings.pagePlaceholder,
    },
    {
      id: F.exhibitionTitle,
      kind: "text",
      label: strings.exhibitionTitleLabel,
      placeholder: strings.exhibitionTitlePlaceholder,
      wide: true,
    },
    {
      id: F.exhibitionVenue,
      kind: "text",
      label: strings.exhibitionVenueLabel,
      placeholder: strings.exhibitionVenuePlaceholder,
      wide: true,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function exhibitionLabels(strings: Strings): ExhibitionLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
    apaCatalogNote: strings.apaCatalogNote,
    mlaCatalogNote: strings.mlaCatalogNote,
    chicagoCatalogNote: strings.chicagoCatalogNote,
  };
}

export default function ExhibitionCitationGeneratorWidget() {
  const strings = useToolUi(EXHIBITION_CITATION_GENERATOR_STRINGS);
  const [source, setSource] = useState<ExhibitionSource>("exhibition");
  const isCatalog = source === "catalog";
  const fields = useMemo(
    () => (isCatalog ? catalogFields(strings) : exhibitionFields(strings)),
    [isCatalog, strings]
  );
  const labels = useMemo(() => exhibitionLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatExhibition(source, values, labels),
    [source, labels]
  );
  const builderStrings = useMemo(
    () => ({
      ...strings,
      emptyPrompt: isCatalog
        ? strings.emptyPromptCatalog
        : strings.emptyPromptExhibition,
    }),
    [isCatalog, strings]
  );

  return (
    <SourceCitationBuilder
      example={isCatalog ? strings.exampleCatalog : strings.exampleExhibition}
      fields={fields}
      format={format}
      intro={
        <PillGroup
          label={strings.sourceLabel}
          name="exhibition-source"
          onChange={setSource}
          options={[
            { value: "exhibition", label: strings.sourceExhibition },
            { value: "catalog", label: strings.sourceCatalog },
          ]}
          value={source}
        />
      }
      isReady={exhibitionIsReady}
      strings={builderStrings}
    />
  );
}
